import { useState, useEffect } from 'react';

// Material UI
import { Box, Grid, Container, Card, Typography, FormControlLabel, Checkbox, Button } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

// Componentes
import AdminLayout from '@components/MainPage/AdminLayout';
import BasicListDoble from '@components/Lists/BasicListDoble';
import CustomLoading from '@components/LinearProgress/CustomLoading';

// Servicios
import UserService from '@services/UserServices';

// Utils
import { isTypePhone, isEmptyOrNullObject } from '@utils/validations';
import { getVars } from '@utils/global';
import { Swal } from '@utils/alerts';

// Data
import { PERFIL_FOCA_ADMIN, PERFIL_PPM_ADMIN } from '@data/constants';

const AdminEventos = (props) => {
  const [user, setUser] = useState({});
  const [checked, setChecked] = useState({ FOCA: false, PPM: false });
  const [canCheck, setCanCheck] = useState({ FOCA: false, PPM: false, BOTH: false, NONE: true });

  const [isLoading, setIsLoading] = useState({ state: false, message: '' });

  useEffect(() => {
    const ambito_perfil = getVars('Token')?.user?.ambito_perfil || [];

    if (ambito_perfil.length) {
      const idPerfiles = ambito_perfil?.map((item) => item?.idPerfil) || [];

      const FOCA = idPerfiles.includes(PERFIL_FOCA_ADMIN);
      const PPM = idPerfiles.includes(PERFIL_PPM_ADMIN);
      const BOTH = FOCA && PPM;
      const NONE = !FOCA && !PPM;

      setCanCheck({ FOCA, PPM, BOTH, NONE });

      if (!BOTH) setChecked({ FOCA, PPM });
    } // eslint-disable-next-line
  }, []);

  const handleUser = (e) => setUser(e);
  const handleClear = () => setUser({});

  const handleChange = ({ target }) => {
    setChecked({ ...checked, [target.name]: target.checked });
  };

  const handleSubmit = async (type) => {
    try {
      if (canCheck.NONE) {
        throw new Error('No tienes el el ambito de perfil necesario para realizar esta accion.');
      }
      const isSave = type === 'save';

      setIsLoading({ state: true, message: isSave ? 'Añadiendo menus' : 'Eliminando menus' });

      const params = { idsPerfil: [], idUsuario: user.id };

      if (canCheck.FOCA && checked.FOCA) params.idsPerfil?.push(PERFIL_FOCA_ADMIN);
      if (canCheck.PPM && checked.PPM) params.idsPerfil?.push(PERFIL_PPM_ADMIN);

      const result = isSave
        ? await UserService.setMenusByRoles(params)
        : await UserService.deleteMenusByRoles(params);
      const { results, message } = result;

      if (results) {
        Swal.fire({ title: message, icon: 'success' });
        if (canCheck.BOTH) setChecked({ FOCA: false, PPM: false });
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsLoading({ state: false, message: '' });
    }
  };

  return (
    <AdminLayout>
      <CustomLoading isOpen={isLoading.state} message={isLoading.message} />

      <Container id="container" maxWidth={false}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={6}>
            <BasicListDoble
              id="id_Usuarios"
              API={UserService.getUsers}
              APIKeys={[
                { id: ['usuarios.Nombre', 'usuarios.Paterno', 'usuarios.Materno'], filter: 'LIKE' },
                { id: 'usuarios.Username', filter: '=' },
              ]}
              header=""
              handleClick={handleUser}
              handleClear={handleClear}
              label="Encuentra y elige al usuario"
              labelNote="Nota: Para realizar una búsqueda debe ingresar un nombre o por el contrario un número de celular."
              finder
              clearData
              doubleSearch
              inputValidation={{ input2: isTypePhone }}
              lengthValidation={{ input2: { filter: '=', value: 10 } }}
              breaks={{ sm: 12, md: 6 }}
              buttonsBreaks={{ sm: 12, md: 4 }}
              config={{ height: 400 }}
              disabled={canCheck.NONE}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <Card sx={{ p: 3, height: '100%' }} className="card-primary">
              <Typography variant="h6" gutterBottom pb={3}>
                Asignar Menús Administrador
              </Typography>

              {canCheck?.BOTH && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', my: 3 }}>
                  <FormControlLabel
                    control={<Checkbox checked={checked.FOCA} onChange={handleChange} name="FOCA" />}
                    label="FOCA"
                    disabled={isEmptyOrNullObject(user)}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={checked.PPM} onChange={handleChange} name="PPM" />}
                    label="PPM"
                    disabled={isEmptyOrNullObject(user)}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, mb: 3 }}>
                {canCheck.NONE ? (
                  <Typography variant="h6" gutterBottom>
                    No tienes el el ambito de perfil necesario para realizar esta accion.
                  </Typography>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="primaryDark"
                      endIcon={<Add />}
                      disabled={canCheck?.BOTH ? !checked.PPM && !checked.FOCA : isEmptyOrNullObject(user)}
                      onClick={() => handleSubmit('save')}
                    >
                      {canCheck?.BOTH
                        ? `Añadir menus`
                        : `Añadir menus ${canCheck?.FOCA ? 'de FOCA' : 'de PPM'} `}
                    </Button>

                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      endIcon={<Delete />}
                      disabled={canCheck?.BOTH ? !checked.PPM && !checked.FOCA : isEmptyOrNullObject(user)}
                      onClick={() => handleSubmit('delete')}
                    >
                      {canCheck?.BOTH
                        ? `Eliminar menus`
                        : `Eliminar menus ${canCheck?.FOCA ? 'de FOCA' : 'de PPM'} `}
                    </Button>
                  </>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </AdminLayout>
  );
};

export default AdminEventos;
